import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getProjects } from '../../../core/state/project/asyncActions';
import { Project } from '../../../core/state/project/types';
import { RootState } from '../../../core/state/state';
import Image from '../../../components/image/Image';
import { BASE_URL } from '../../../core/constants/api';
import NoResultsPage from '../../../components/no-results-page/NoResultsPage';
import { history } from '../../../core/state/history';
import InteractiveElement from '../../../components/interactive-element/InteractiveElement';
import Search from '../../../components/search/Search';
import { projectListSorter } from '../../../core/utils/projectListSorter';
import { ProjectSortTypes } from '../../../core/enums/ProjectSortTypes';
import { getHighlightedText } from '../getHighlightedText';

import './ProjectListingDesktop.scss';

const ProjectListingDesktop: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [search, setSearch] = React.useState('');
  const { projects, projectsCopy: projectList } = useSelector((state: RootState) => state.projects);
  const noPermissions = projects && projectList?.length === 0;
  const noSearchResults = search.length > 2 && projects?.length === 0 && Boolean(projectList?.length);

  React.useEffect(() => {
    dispatch(getProjects(true));
  }, [dispatch]);

  const handleSearch = (value: string) => {
    setSearch(value);
    if (projectList) {
      if (value.length > 2) {
        projectListSorter(projectList, undefined, undefined, value);
      } else {
        projectListSorter(projectList, 'desc', ProjectSortTypes.ProjectNumber);
      }
    }
  };

  return (
    <div className="project-listing-desktop">
      <div className="project-listing-desktop__header">
        <div>{search.length > 2 ? t('projectListing.header.searchResults') : t('sidebar.link.allProjects')}</div>
        {!noPermissions && (
          <div className="project-listing-desktop__search">
            <Search
              id="project-search"
              name="project-search"
              value={search}
              placeholder={t('common.search')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
              onReset={() => handleSearch('')}
            />
          </div>
        )}
      </div>
      <p className="project-listing-desktop__legend">{`${t('common.projectName')} / ${t('projectSettings.projectInfo.projectNumber')} / ${t(
        'projectSettings.projectInfo.constructionSite'
      )}`}</p>
      <div className="project-listing-desktop__list">
        {projects?.map((project: Project) => (
          <InteractiveElement
            key={project.code}
            className="project-listing-desktop__project"
            onClick={() => history.push(`/projects/${project.id}/bookings`)}
          >
            <Image className="project-listing__project-img" src={`${BASE_URL}/${project.photo}`} alt="" />
            <div className="d-dlex">
              <span className="project-listing-desktop__project-name text-truncate">{getHighlightedText(project.name, search)}</span>
              <div className="d-flex">
                <span className="project-listing-desktop__project-code mr-1">{getHighlightedText(project.code, search)}</span>
                <span className="project-listing-desktop__project-area text-truncate">{getHighlightedText(project.constructionArea, search)}</span>
              </div>
            </div>
          </InteractiveElement>
        ))}
      </div>
      {noPermissions && (
        <NoResultsPage title={t('projectListing.noPermissions.title')} description={t('projectListing.noPermissions.subtitle')} icon="stop" />
      )}
      {noSearchResults && (
        <NoResultsPage title={t('projectListing.noResults.title')} description={t('projectListing.noResults.text')} icon="search" />
      )}
      {!projects && <NoResultsPage title={t('common.noProjects')} description={t('projectListing.noProjects.text')} icon="projects" />}
    </div>
  );
};

export default ProjectListingDesktop;
